import { widget } from "@foretagsplatsen/widgetjs";

/**
 * Base for all form fields. Renders a form field followed by a help div
 * where warning/info/error messages can be displayed.
 *
 * _Options:_
 * spec.attribute	Dot separated string that gives the path to a
 *					attribute in an object to display/modify
 *					Eg. "contactInfo.email".
 *
 * spec.value		Default/Current value for field
 *
 * spec.validator	Function that take a value as argument and
 *					returns true if value is validate
 *
 * spec.disabled	Field is marked as disabled if true.
 *
 * spec.containerCssClass Optional CSS class used for the wrapping div around
 * the form field.
 *
 * _Note:_ Override ´my.renderFieldOn()´ to render the actual input field. Also set value
 * using `setValue` when value change.
 */
function formFieldWidget(spec, my) {
	my ||= {};
	spec ||= {};

	let value = spec.value;
	const validator = spec.validator || (() => {});
	const attribute = spec.attribute;
	let error;
	const help = spec.help;
	let enabled = !spec.disabled;
	const containerCssClass = spec.containerCssClass || "";

	const that = widget(spec, my);

	that.mutator = spec.mutator;
	that.accessor = spec.accessor;
	that.attribute = attribute;

	my.attributes = spec.attributes || {};
	my.validateOnBlur = true;

	that.setValidateOnBlur = function (boolean) {
		my.validateOnBlur = boolean;
	};

	that.getValidateOnBlur = function () {
		return my.validateOnBlur;
	};

	that.getValue = function () {
		return value;
	};

	that.setValue = function (newValue, preventValidation) {
		value = newValue;

		if (!preventValidation) {
			that.update();
			that.validate();
		}

		that.trigger("change", value, that, that.valid);
	};

	that.enabled = function (flag) {
		if (flag === undefined) {
			return enabled;
		}

		enabled = flag;

		if (!enabled) {
			my.clearError();
		}

		that.update();
		return enabled;
	};

	that.valid = function () {
		return !error;
	};

	my.callValidator = function (val) {
		val = val === undefined ? that.getValue() : val;

		validator(val);
	};

	that.isValid = function (val) {
		try {
			my.callValidator(val);
			return true;
		} catch {
			return false;
		}
	};

	that.validate = function (val) {
		error = null;

		try {
			my.callValidator(val);
		} catch (newError) {
			error = newError;
		}

		if (error) {
			my.displayError();
		} else {
			my.clearError();
		}

		return !error;
	};

	my.renderFieldOn = function () {};

	my.renderInlineHelpOn = function (html) {
		my.inlineHelpSpan = html.span({
			klass: "help-block error",
			"data-test": "inline-help",
		});
	};

	my.renderBlockHelpOn = function (html) {
		if (!help) {
			return;
		}

		my.blockHelpSpan = html.span({
			klass: "help-block",
			"data-test": "block-help",
		});

		my.blockHelpSpan.render(help);
	};

	that.renderContentOn = function (html) {
		html.div({
			klass: containerCssClass,
			"data-test": "form-field",
		}).render((html) => {
			my.renderFieldOn(html);
			my.renderInlineHelpOn(html);
			my.renderBlockHelpOn(html);
		});
	};

	// eslint-disable-next-line no-shadow -- bad practice, we should fix this
	my.displayHelp = function (error) {
		if (!my.inlineHelpSpan) {
			return;
		}

		my.inlineHelpSpan.asJQuery().empty();
		my.inlineHelpSpan.render(error.message || error);
	};

	my.displayError = function () {
		if (!that.enabled()) {
			return;
		}

		that.trigger("validationStateChange", that, error);
		my.displayHelp(error);
	};

	my.clearError = function () {
		error = null;
		that.trigger("validationStateChange", that, null);
		if (!my.inlineHelpSpan) {
			return;
		}
		my.inlineHelpSpan.asJQuery().empty();
	};

	return that;
}

export default formFieldWidget;
